/**
 * i18next with the copy of design/i18n/<locale>.json (HANDOFF §0.9, L1). Keys are flat (`misc.try_again`): no key
 * or namespace separator. Placeholders are ICU-style single braces (`{productName}`). React escapes the output,
 * so i18next does not. Plurals are i18next's v4 JSON (`_one`, `_few`, `_many`, `_other`: CLDR categories through
 * `Intl.PluralRules`).
 *
 * A translation's catalogues carry en's text for what they lack (merged at build time, vite/locales.ts
 * `localize`); `fallbackLng: "en"` only covers the pending keys and a key named before its catalogue loaded.
 */
import i18next, { type i18n as I18n } from "i18next";
import { initReactI18next } from "react-i18next";
import type en from "../../../../../design/i18n/en.json";
import { pendingCopy } from "./pending";
import { setActiveLocale, SOURCE_LOCALE } from "./locale";

// Typed copy keys: t("misc.try_again") is checked against design/i18n/en.json at compile time.
declare module "i18next" {
  interface CustomTypeOptions {
    defaultNS: "translation";
    resources: {
      translation: typeof en & typeof pendingCopy;
    };
    keySeparator: false;
    nsSeparator: false;
    interpolationPrefix: "{";
    interpolationSuffix: "}";
    returnNull: false;
  }
}

export type Copy = Record<string, string>;

/**
 * An instance with en's first catalogue (`copy`), and `lng`'s when another locale starts (its catalogue in
 * `resources`; the locale runtime loads it before the first render).
 */
export function createI18n(copy: Copy, lng = SOURCE_LOCALE, resources: Record<string, Copy> = {}): I18n {
  const instance = i18next.createInstance();
  instance.on("languageChanged", (l) => setActiveLocale(l));
  void instance.use(initReactI18next).init({
    lng,
    fallbackLng: SOURCE_LOCALE,
    // The app's first catalogue; the rest and the route groups' namespaces are added later (locale-runtime.ts).
    resources: {
      ...Object.fromEntries(Object.entries(resources).map(([l, c]) => [l, { translation: c }])),
      [SOURCE_LOCALE]: { translation: { ...pendingCopy, ...copy } },
    },
    keySeparator: false,
    nsSeparator: false,
    interpolation: { prefix: "{", suffix: "}", escapeValue: false },
    returnNull: false,
    initAsync: false,
    saveMissing: import.meta.env.DEV,
    missingKeyHandler: (_lngs, _ns, key) => {
      console.error(`i18n: missing copy key "${key}"`);
    },
  });
  setActiveLocale(instance.language);
  return instance;
}
